import { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import clsx from "clsx";

interface PollAnswerFormProps {
  programItemId: string;
  groups: any[];
  options: any[];
  allowAbstain: boolean;
  existingAnswers: any[];
}

export default function PollAnswerForm({
  programItemId,
  groups,
  options,
  allowAbstain,
  existingAnswers,
}: PollAnswerFormProps) {
  const [selectedOptions, setSelectedOptions] = useState<{
    [groupId: string]: string[];
  }>({});
  const [abstain, setAbstain] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (existingAnswers.length > 0) {
      const saved = existingAnswers[0];
      setAbstain(saved.not_participating);
      const grouped: Record<string, string[]> = {};

      (saved.selected_option_ids || []).forEach((id: string) => {
        const option = options.find((o) => o.id === id);
        if (option?.option_group_id) {
          if (!grouped[option.option_group_id]) {
            grouped[option.option_group_id] = [];
          }
          grouped[option.option_group_id].push(option.id);
        }
      });

      setSelectedOptions(grouped);
    }
  }, [existingAnswers, options]);

  const handleToggleOption = (
    groupId: string,
    optionId: string,
    multiple: boolean
  ) => {
    setAbstain(false); // Deaktiviert "nicht dabei"
    setSelectedOptions((prev) => {
      const current = prev[groupId] || [];
      const updated = multiple
        ? current.includes(optionId)
          ? current.filter((id) => id !== optionId)
          : [...current, optionId]
        : [optionId];

      return { ...prev, [groupId]: updated };
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    setSaved(false);

    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    // 🛡️ Pflicht-Gruppenprüfung
    const missingRequiredGroups = groups
      .filter((group) => group.is_required)
      .filter((group) => {
        const selected = selectedOptions[group.id] || [];
        return selected.length === 0;
      });

    if (missingRequiredGroups.length > 0) {
      alert("❗Bitte wähle eine Option in allen Pflichtgruppen aus.");
      setSaving(false);
      return;
    }

    // 🔄 Vorherige Antworten löschen
    await supabase
      .from("trip_program_poll_responses")
      .delete()
      .eq("user_id", user.id)
      .eq("program_item_id", programItemId);

    // ➕ Antwort speichern
    if (abstain) {
      await supabase.from("trip_program_poll_responses").insert({
        user_id: user.id,
        program_item_id: programItemId,
        not_participating: true,
      });
    } else {
      const selectedIds = Object.values(selectedOptions).flat();
      if (selectedIds.length > 0) {
        await supabase.from("trip_program_poll_responses").insert({
          user_id: user.id,
          program_item_id: programItemId,
          selected_option_ids: selectedIds,
          not_participating: false,
        });
      }
    }

    setSaving(false);
    setSaved(true);
  };

  const hasTooManySelections = (groupId: string): boolean => {
    const group = groups.find((g) => g.id === groupId);
    const selected = selectedOptions[groupId] || [];
    return (
      group?.max_selections !== null &&
      group?.max_selections > 0 &&
      selected.length > group.max_selections
    );
  };

  const isValid = groups.every(
    (group) =>
      !hasTooManySelections(group.id) &&
      (!group.is_required || (selectedOptions[group.id]?.length ?? 0) > 0)
  );

  return (
    <div className="space-y-6">
      {groups.map((group) => {
        const groupOptions = options.filter(
          (opt) => opt.option_group_id === group.id
        );
        const selected = selectedOptions[group.id] || [];
        const isMultiple =
          group.max_selections === null || group.max_selections > 1;

        return (
          <fieldset key={group.id} className="border rounded p-4">
            <legend className="font-semibold mb-2">
              {group.title || "Auswahlgruppe"}
              {group.is_required && <span className="text-red-500"> *</span>}
            </legend>

            <ul className="space-y-2">
              <div className="text-red-600 text-sm mt-1">
                Wähle maximal {group.max_selections} aus.
              </div>
              {groupOptions.map((opt) => (
                <li key={opt.id}>
                  <label className="flex items-center gap-2">
                    <input
                      type={isMultiple ? "checkbox" : "radio"}
                      name={group.id}
                      value={opt.id}
                      checked={selected.includes(opt.id)}
                      onChange={() =>
                        handleToggleOption(group.id, opt.id, isMultiple)
                      }
                    />
                    <span>{opt.label}</span>
                    {opt.price && (
                      <span className="text-sm text-gray-500">
                        ({opt.price.toFixed(2)} CHF)
                      </span>
                    )}
                  </label>
                  {opt.note && (
                    <div className="ml-6 text-sm text-gray-500 italic">
                      {opt.note}
                    </div>
                  )}
                </li>
              ))}
              {hasTooManySelections(group.id) && (
                <div className="text-red-600 text-sm mt-1">
                  Du darfst maximal {group.max_selections} Optionen auswählen.
                </div>
              )}
            </ul>
          </fieldset>
        );
      })}

      {allowAbstain && (
        <div className="border rounded p-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={abstain}
              onChange={() => {
                setAbstain(!abstain);
                if (!abstain) setSelectedOptions({});
              }}
            />
            <span>Ich bin nicht dabei</span>
          </label>
        </div>
      )}

      <button
        disabled={!isValid}
        className={clsx(
          "px-4 py-2 rounded",
          isValid
            ? "bg-green-600 text-white hover:bg-green-700"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        )}
      >
        Antwort speichern
      </button>

      {saved && (
        <p className="text-green-600 text-sm mt-2">✅ Antwort gespeichert</p>
      )}
    </div>
  );
}
